import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon } from '@/assets/icons/CalendarIcon';
import { MapPinIcon } from '@/assets/icons/MapPinIcon';
import { ArrowRightIcon } from '@/assets/icons/ArrowRightIcon';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { EVENTS_STRINGS } from '../constants/events-strings';
import { FeaturedEventBadge } from './FeaturedEventBadge';
import { eventDetailPath } from '@/lib/constants/routes';
import type { PublicEvent } from '../types';
import type { EventDetailLinkState } from '../event-detail-back';

export interface EventCardProps {
  event: PublicEvent;
  /**
   * Show the `#FED13D` FEATURED EVENT badge above the title. On the public
   * pages the featured event is the FIRST CARD IN THE GRID carrying this badge -
   * there is no full-width banner on a public page.
   */
  featured?: boolean;
  /** Show a green "Registered" badge above the title. */
  isRegistered?: boolean;
  /**
   * Size the cover photo by the uploaded 16:9 ratio (1200x675, data-fields.md)
   * instead of the events grid's fixed 180px height. For grids whose cards get
   * much wider than the events page's, where a fixed height turns the photo
   * into a thin strip (UX sheet v2, dashboard on wide monitors).
   */
  fluidCover?: boolean;
  /**
   * Show the Public / Members Only tag. Off on the dashboard: members do not
   * need the access type there (UX sheet v2).
   */
  showAccessTag?: boolean;
  /** Router state for View Details, so the detail page's back link returns to the caller. */
  linkState?: EventDetailLinkState;
}

/*
 * Figma I418:15577 "Investor Member EventCard": 16px radius, 1px outline, no
 * shadow, flush 180px cover photo, 16px body padding, 12px internal gap.
 *
 * `!` on the button utilities is load-bearing: they collide with the ones
 * `Button`'s `outline` variant emits (`rounded-button px-8 py-4`), and Tailwind
 * gives colliding utilities equal specificity, so without `!` which one applies
 * depends on stylesheet emit order rather than class-attribute order. Overriding
 * the geometry rather than adding a Button variant keeps the variant's themed
 * `btn-outline-*` tokens, its five states, its `cursor-pointer`, and its
 * focus-visible ring.
 */
const VIEW_DETAILS_CLASSES = 'h-9 rounded-lg! px-4! py-0! text-xs! flex items-center';

/**
 * One card of the public events grid (FR-34). The same card renders whether or
 * not the event is featured - `featured` only adds the badge, so the fallback
 * when no featured event is resolved is this component with the flag off.
 *
 * Colours are semantic tokens throughout, so the card is legible on a dark
 * surface as well as a light one.
 */
const BADGE_BASE =
  'inline-flex items-center rounded px-2.5 py-1 text-[0.6875rem] font-medium leading-none whitespace-nowrap';
/** Class and label per access type, resolved once so the two never drift apart. */
const ACCESS_BADGE: Partial<
  Record<NonNullable<PublicEvent['accessType']>, { className: string; label: string }>
> = {
  public: {
    className: `${BADGE_BASE} bg-[#FED13D] text-[#1A1A1A]`,
    label: EVENTS_STRINGS.TAG_PUBLIC,
  },
  members_only: {
    className: `${BADGE_BASE} bg-[#4F0A33] text-white`,
    label: EVENTS_STRINGS.TAG_MEMBERS_ONLY,
  },
};

export function EventCard({
  event,
  featured = false,
  isRegistered = false,
  fluidCover = false,
  showAccessTag = true,
  linkState,
}: EventCardProps) {
  // One tag at most, picked once: Featured > Registered > access type.
  const accessBadge = event.accessType ? ACCESS_BADGE[event.accessType] : undefined;
  let tag: ReactNode = null;
  if (featured) {
    tag = <FeaturedEventBadge size="card" />;
  } else if (isRegistered) {
    tag = (
      <span className={`${BADGE_BASE} bg-green text-white`}>{EVENTS_STRINGS.TAG_REGISTERED}</span>
    );
  } else if (showAccessTag && accessBadge) {
    tag = <span className={accessBadge.className}>{accessBadge.label}</span>;
  }

  const dateRow = (
    <div className="flex items-center gap-2">
      <CalendarIcon size={14} className="text-text-secondary shrink-0" />
      {/* Wraps rather than truncates: the label ends with the time, which
          is the part a narrow card would otherwise cut off (UX sheet v2). */}
      <p className="text-text-secondary text-sm leading-[1.125rem]">{event.dateLabel}</p>
    </div>
  );
  // Location is always a tag in the footer, beside any status tag, so every
  // card's footer row is filled whether or not it has a status (UX sheet v2).
  const locationTag = (
    <span
      className={`${BADGE_BASE} border-border-strong text-text-primary shrink-0 gap-1 border`}
    >
      <MapPinIcon size={10} className="shrink-0" />
      {event.location}
    </span>
  );
  const viewDetails = (
    <Button asChild variant="outline" className={VIEW_DETAILS_CLASSES}>
      <Link to={eventDetailPath(event.id)} state={linkState}>
        <span className="inline-flex items-center gap-2">
          {EVENTS_STRINGS.VIEW_DETAILS}
          <ArrowRightIcon size={9} />
        </span>
      </Link>
    </Button>
  );

  return (
    <article className="border-border-strong bg-surface-card flex flex-col overflow-hidden rounded-2xl border">
      {/* Nothing overlays the cover: the client wants the photo clean, so the
          tags sit in the footer instead (UX sheet v2, MYN-251). */}
      <div className={`bg-input-disabled relative w-full shrink-0 ${fluidCover ? 'aspect-video' : 'h-[11.25rem]'}`}>
        {/* An event saved before covers were required has none (MYN-343): leave the neutral surface, never an empty image. */}
        {event.imageUrl && (
          <img src={event.imageUrl} alt="" className="absolute inset-0 size-full object-cover" />
        )}
      </div>
      <div className="flex flex-col gap-2 p-4">
        {/* The title truncates to one line, so the full title shows on hover.
            The text stays in the DOM, so screen readers get it whole. */}
        <Tooltip content={event.title}>
          <h3 className="text-text-primary truncate text-[0.9375rem] leading-[1.375rem] font-semibold">
            {event.title}
          </h3>
        </Tooltip>
        {dateRow}
        <div className="border-divider mt-1.5 flex items-center gap-2 border-t pt-3">
          <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
            {locationTag}
            {tag}
          </div>
          {viewDetails}
        </div>
      </div>
    </article>
  );
}
